import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Maya's Character Profile and Story Elements
export interface MayaStoryElement {
  id: string;
  type: 'challenge' | 'insight' | 'success' | 'lesson-learned';
  title: string;
  description: string;
  context: string;
  outcome?: string;
  lessonRelevance: string[];
}

export interface MayaPersonality {
  traits: string[];
  communicationStyle: string[];
  values: string[];
  growth_areas: string[];
  expertise: string[];
}

export interface MayaWorkContext {
  organization: string;
  role: string;
  responsibilities: string[];
  challenges: string[];
  stakeholders: string[];
  communications: {
    type: string;
    frequency: string;
    audience: string;
    purpose: string;
  }[];
}

// Maya's emotional states for different situations
export type MayaMood = 
  | 'encouraging' 
  | 'excited'
  | 'thoughtful'
  | 'celebratory'
  | 'supportive'
  | 'confident'
  | 'empathetic';

// Maya's development phases
export type MayaPhase = 'overwhelmed' | 'discovering' | 'learning' | 'confident' | 'mentoring';

export interface MayaCharacterState {
  // Character profile
  personality: MayaPersonality;
  workContext: MayaWorkContext;
  
  // Story progression
  currentStoryArc: string;
  storyElements: MayaStoryElement[];
  unlockedElements: string[];
  
  // User interaction with Maya
  mentorshipLevel: 'introduction' | 'learning' | 'practicing' | 'mastering';
  sharedExperiences: string[];
  personalConnections: Record<string, string>;
  relationshipLevel: 1 | 2 | 3 | 4 | 5; // How well Maya knows the user
  
  // Maya's guidance system
  activeGuidance: string[];
  completedGuidance: string[];
  adaptiveInsights: string[];
  
  // Maya's development phase
  currentPhase: MayaPhase;
  
  // Interaction tracking
  lastInteraction: string | null;
  interactionCount: number;
}

interface MayaCharacterContextType extends MayaCharacterState {
  // Story progression
  advanceStoryArc: (newArc: string) => void;
  unlockStoryElement: (elementId: string) => void;
  addStoryElement: (element: MayaStoryElement) => void;
  
  // User relationship building
  progressMentorshipLevel: () => void;
  addSharedExperience: (experience: string) => void;
  createPersonalConnection: (key: string, connection: string) => void;
  
  // Guidance system
  activateGuidance: (guidanceId: string) => void;
  completeGuidance: (guidanceId: string) => void;
  addAdaptiveInsight: (insight: string) => void;
  
  // Maya's responses
  getMayaResponse: (context: string, userInput?: string) => string;
  getMayaEncouragement: (achievement: string) => string;
  getMayaGuidance: (challenge: string) => string;
  
  // EMERGENCY FIX: Missing functions that components expect
  getCurrentMood: () => MayaMood;
  getPersonalizedContent: (baseContent: string) => string;
  recordInteraction: (interactionId: string) => void;
  shouldShowMaya: (context: string) => boolean;
  
  // Phase management
  updatePhase: (phase: MayaPhase) => void;
  
  // Utility
  resetMayaRelationship: () => void;
  exportMayaJourney: () => string;
}

const MayaCharacterContext = createContext<MayaCharacterContextType | undefined>(undefined);

// Maya's core character data
const mayaPersonality: MayaPersonality = {
  traits: ['Empathetic', 'Determined', 'Collaborative', 'Growth-minded', 'Authentic'],
  communicationStyle: ['Warm but professional', 'Story-driven', 'Solution-focused', 'Inclusive'],
  values: ['Community impact', 'Inclusive communication', 'Continuous learning', 'Authentic relationships'],
  growth_areas: ['Confidence in leadership communications', 'Managing difficult conversations', 'Time management with communications'],
  expertise: ['Community outreach', 'Program coordination', 'Stakeholder relationships', 'Cultural sensitivity']
};

const mayaWorkContext: MayaWorkContext = {
  organization: 'Hope Gardens Community Center',
  role: 'Community Outreach Coordinator',
  responsibilities: [
    'Coordinating community programs',
    'Managing stakeholder relationships',
    'Creating program communications',
    'Supporting volunteer recruitment',
    'Facilitating community partnerships'
  ],
  challenges: [
    'Email overwhelm from multiple stakeholders',
    'Balancing formal and informal communication tones',
    'Managing communications for diverse audiences',
    'Time constraints affecting communication quality'
  ],
  stakeholders: ['Community members', 'Board of Directors', 'Local partners', 'Volunteers', 'Donors', 'Government liaisons'],
  communications: [
    { type: 'Email updates', frequency: 'Weekly', audience: 'Volunteers', purpose: 'Program coordination' },
    { type: 'Board reports', frequency: 'Monthly', audience: 'Board members', purpose: 'Impact reporting' },
    { type: 'Community newsletters', frequency: 'Quarterly', audience: 'Community', purpose: 'Engagement' },
    { type: 'Partner communications', frequency: 'As needed', audience: 'Local organizations', purpose: 'Collaboration' }
  ]
};

const initialStoryElements: MayaStoryElement[] = [
  {
    id: 'email-overwhelm-challenge',
    type: 'challenge',
    title: 'The Great Email Overwhelm',
    description: 'Maya struggles with managing communications to diverse stakeholders',
    context: 'Monday morning inbox with 47 unread emails from board members, volunteers, and community partners',
    outcome: 'Realized need for systematic approach to communication',
    lessonRelevance: ['voice-foundations', 'audience-analysis']
  },
  {
    id: 'board-presentation-insight',
    type: 'insight',
    title: 'Finding Her Voice in the Boardroom',
    description: 'Maya discovers the power of adapting her communication style',
    context: 'Monthly board meeting where Maya needs to present program results',
    outcome: 'Successfully adapted from casual to professional tone while maintaining authenticity',
    lessonRelevance: ['tone-adaptation', 'personal-voice-workshop']
  }
];

const initialState: MayaCharacterState = {
  personality: mayaPersonality,
  workContext: mayaWorkContext,
  currentStoryArc: 'discovering-voice',
  storyElements: initialStoryElements,
  unlockedElements: ['email-overwhelm-challenge'],
  mentorshipLevel: 'introduction',
  sharedExperiences: [],
  personalConnections: {},
  relationshipLevel: 1,
  activeGuidance: ['voice-foundations-intro'],
  completedGuidance: [],
  adaptiveInsights: [],
  currentPhase: 'discovering',
  lastInteraction: null,
  interactionCount: 0
};

export const MayaCharacterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MayaCharacterState>(initialState);

  // Story progression
  const advanceStoryArc = useCallback((newArc: string) => {
    setState(prev => ({ ...prev, currentStoryArc: newArc }));
  }, []);

  const unlockStoryElement = useCallback((elementId: string) => {
    setState(prev => ({
      ...prev,
      unlockedElements: [...prev.unlockedElements, elementId]
    }));
  }, []);

  const addStoryElement = useCallback((element: MayaStoryElement) => {
    setState(prev => ({
      ...prev,
      storyElements: [...prev.storyElements, element]
    }));
  }, []);

  // User relationship building
  const progressMentorshipLevel = useCallback(() => {
    setState(prev => {
      const levels = ['introduction', 'learning', 'practicing', 'mastering'] as const;
      const currentIndex = levels.indexOf(prev.mentorshipLevel);
      const nextLevel = currentIndex < levels.length - 1 ? levels[currentIndex + 1] : prev.mentorshipLevel;
      return { ...prev, mentorshipLevel: nextLevel };
    });
  }, []);

  const addSharedExperience = useCallback((experience: string) => {
    setState(prev => ({
      ...prev,
      sharedExperiences: [...prev.sharedExperiences, experience]
    }));
  }, []);

  const createPersonalConnection = useCallback((key: string, connection: string) => {
    setState(prev => ({
      ...prev,
      personalConnections: { ...prev.personalConnections, [key]: connection }
    }));
  }, []);

  // Guidance system
  const activateGuidance = useCallback((guidanceId: string) => {
    setState(prev => ({
      ...prev,
      activeGuidance: [...prev.activeGuidance, guidanceId]
    }));
  }, []);

  const completeGuidance = useCallback((guidanceId: string) => {
    setState(prev => ({
      ...prev,
      activeGuidance: prev.activeGuidance.filter(id => id !== guidanceId),
      completedGuidance: [...prev.completedGuidance, guidanceId]
    }));
  }, []);

  const addAdaptiveInsight = useCallback((insight: string) => {
    setState(prev => ({
      ...prev,
      adaptiveInsights: [...prev.adaptiveInsights, insight]
    }));
  }, []);

  // Maya's responses based on context and user relationship
  const getMayaResponse = useCallback((context: string, userInput?: string): string => {
    const responses = {
      'voice-foundations': 'I remember when I first started at Hope Gardens, I felt like I was juggling so many different communication styles. Let me share what I\'ve learned about finding your authentic voice...',
      'tone-adaptation': 'You know, I used to think I had to be someone completely different when talking to our board versus our volunteers. But I discovered something amazing - you can adapt your tone while staying true to who you are.',
      'audience-analysis': 'Understanding your audience changed everything for me. When I really started listening to what our community members, donors, and partners needed, my communications became so much more effective.',
      'workshop-encouragement': 'I\'m so excited to work through this with you! Remember, this is your journey - I\'m just here to share what I\'ve learned along the way.',
      'default': 'Every challenge you face in communication, I\'ve probably faced too. Let\'s figure this out together!'
    };
    
    return responses[context as keyof typeof responses] || responses.default;
  }, [state.mentorshipLevel, state.sharedExperiences]);

  const getMayaEncouragement = useCallback((achievement: string): string => {
    const encouragements = {
      'lesson-complete': 'You\'re making such great progress! I can see you really understanding these concepts.',
      'voice-discovery': 'Finding your authentic voice is such a powerful moment - I remember when it clicked for me too!',
      'tone-mastery': 'The way you\'re adapting your communication while staying true to yourself reminds me of my own growth journey.',
      'audience-insight': 'That insight about your audience is spot-on! You\'re developing the same intuition that transformed my work.',
      'default': 'I\'m so proud of the progress you\'re making. Keep going!'
    };
    
    return encouragements[achievement as keyof typeof encouragements] || encouragements.default;
  }, []);

  const getMayaGuidance = useCallback((challenge: string): string => {
    const guidance = {
      'tone-confusion': 'I totally get this! When I first started, I thought being professional meant being cold. Let me show you how to find that sweet spot...',
      'audience-uncertainty': 'Audience analysis used to overwhelm me too. Let\'s break it down into manageable pieces, just like I learned to do.',
      'voice-inconsistency': 'Consistency was my biggest struggle. Here\'s the framework that helped me develop my reliable voice...',
      'confidence-issues': 'I used to doubt my communication skills all the time. Want to hear about what shifted that for me?',
      'default': 'I\'ve been where you are. Let me share what helped me through this exact challenge.'
    };
    
    return guidance[challenge as keyof typeof guidance] || guidance.default;
  }, []);

  // Utility functions
  const resetMayaRelationship = useCallback(() => {
    setState(initialState);
  }, []);

  // EMERGENCY FIX: Implement missing functions
  const getCurrentMood = useCallback((): MayaMood => {
    const { mentorshipLevel, relationshipLevel } = state;
    
    // Mood based on mentorship level and relationship
    const moodMap = {
      introduction: ['empathetic', 'supportive'],
      learning: ['encouraging', 'excited'],
      practicing: ['thoughtful', 'confident'],
      mastering: ['celebratory', 'confident']
    } as const;

    const possibleMoods = moodMap[mentorshipLevel] || ['supportive'];
    
    // Higher relationship level = more positive moods
    if (relationshipLevel >= 4) {
      return possibleMoods[possibleMoods.length - 1] as MayaMood; // Most positive
    } else if (relationshipLevel >= 2) {
      return possibleMoods[Math.floor(possibleMoods.length / 2)] as MayaMood; // Middle
    } else {
      return possibleMoods[0] as MayaMood; // Most supportive
    }
  }, [state]);

  const getPersonalizedContent = useCallback((baseContent: string): string => {
    const { relationshipLevel, mentorshipLevel } = state;
    
    // Personalization based on relationship level
    const personalizations = {
      1: { prefix: "Hi there! ", tone: "supportive" },
      2: { prefix: "Hey! ", tone: "friendly" },
      3: { prefix: "Hi friend! ", tone: "warm" },
      4: { prefix: "Hey there, my friend! ", tone: "encouraging" },
      5: { prefix: "Hi! It's so great to see you again! ", tone: "excited" }
    };

    const personal = personalizations[relationshipLevel] || personalizations[1];
    
    // Mentorship level adjustments
    let adjustedContent = baseContent;
    if (mentorshipLevel === 'mastering') {
      adjustedContent = adjustedContent.replace(/I'm still learning/g, "I've learned");
      adjustedContent = adjustedContent.replace(/I hope/g, "I know");
    }
    
    return `${personal.prefix}${adjustedContent}`;
  }, [state]);

  const recordInteraction = useCallback((interactionId: string) => {
    setState(prev => ({
      ...prev,
      lastInteraction: new Date().toISOString(),
      interactionCount: prev.interactionCount + 1,
      relationshipLevel: Math.min(5, Math.floor(prev.relationshipLevel + 0.1)) as 1 | 2 | 3 | 4 | 5
    }));
  }, []);

  const shouldShowMaya = useCallback((context: string): boolean => {
    const { mentorshipLevel, relationshipLevel } = state;

    // Context-specific logic for when Maya should appear
    switch (context) {
      case 'hub-introduction':
        return mentorshipLevel === 'introduction' || relationshipLevel === 1;
      case 'lesson-guidance':
        return mentorshipLevel !== 'mastering'; // Less guidance when mastering
      case 'story-sharing':
        return mentorshipLevel === 'learning' || mentorshipLevel === 'practicing';
      case 'artifact-support':
        return mentorshipLevel === 'practicing' || mentorshipLevel === 'mastering';
      case 'celebration':
        return true; // Maya always celebrates achievements
      default:
        return relationshipLevel >= 2;
    }
  }, [state]);

  // Phase management
  const updatePhase = useCallback((phase: MayaPhase) => {
    setState(prev => ({ ...prev, currentPhase: phase }));
  }, []);

  const exportMayaJourney = useCallback(() => {
    return JSON.stringify({
      storyProgress: state.currentStoryArc,
      unlockedElements: state.unlockedElements,
      mentorshipLevel: state.mentorshipLevel,
      sharedExperiences: state.sharedExperiences,
      personalConnections: state.personalConnections,
      relationshipLevel: state.relationshipLevel,
      currentPhase: state.currentPhase
    }, null, 2);
  }, [state]);

  const contextValue: MayaCharacterContextType = {
    ...state,
    advanceStoryArc,
    unlockStoryElement,
    addStoryElement,
    progressMentorshipLevel,
    addSharedExperience,
    createPersonalConnection,
    activateGuidance,
    completeGuidance,
    addAdaptiveInsight,
    getMayaResponse,
    getMayaEncouragement,
    getMayaGuidance,
    // EMERGENCY FIX: Add missing functions
    getCurrentMood,
    getPersonalizedContent,
    recordInteraction,
    shouldShowMaya,
    updatePhase,
    resetMayaRelationship,
    exportMayaJourney
  };

  return (
    <MayaCharacterContext.Provider value={contextValue}>
      {children}
    </MayaCharacterContext.Provider>
  );
};

export const useMayaCharacter = () => {
  const context = useContext(MayaCharacterContext);
  if (context === undefined) {
    throw new Error('useMayaCharacter must be used within a MayaCharacterProvider');
  }
  return context;
};